import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { resolveReturnTo } from '@/auth/returnTo';
import { CenterCard } from '@/components/CenterCard';

/**
 * `/login` — the ecosystem's single visible login entry (EAA-1765).
 *
 * Any app that finds the user unauthenticated sends the browser here with
 * `?returnTo=<deep link>`. The portal kicks off the real Keycloak sign-in
 * (Authorization Code + PKCE via the auth-client), stashing the validated
 * `returnTo` in the OIDC `state`; `CallbackPage` reads it back and returns the
 * browser to that exact link. `returnTo` is validated against the allowlist
 * BEFORE use (open-redirect guard).
 *
 * If the user already has a portal session, we skip Keycloak entirely: an
 * allowlisted external `returnTo` redirects immediately, otherwise we land on
 * the launcher. The AuthProvider guard deliberately skips `/login` so this page
 * owns the flow (it must not lose the external `returnTo`).
 */
export function LoginPage() {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const { authenticated, client } = useAuth();
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        const returnTo = new URLSearchParams(location.search).get('returnTo');
        const resolved = resolveReturnTo(returnTo, config.returnToAllowlist ?? []);

        if (authenticated) {
            // Live session already — no Keycloak round-trip needed.
            if (resolved.kind === 'external') {
                window.location.replace(resolved.url);
            } else {
                navigate(resolved.path, { replace: true });
            }
            return;
        }

        // No session → Keycloak. Preserve the ORIGINAL returnTo in state so the
        // callback can bounce back externally; an off-allowlist value is dropped
        // to the launcher path here so nothing unsafe ever reaches state.
        const stateReturnTo = resolved.kind === 'external' ? resolved.url : resolved.path;
        void client.login(stateReturnTo);
    }, [authenticated, client, config.returnToAllowlist, location.search, navigate]);

    return (
        <CenterCard title={t('profile.login.redirectingTitle')}>
            <p>{t('profile.login.pleaseWait')}</p>
        </CenterCard>
    );
}
